import React, { useState, useEffect } from 'react';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { Form, Modal, Select, Input, InputNumber, Space } from 'antd';
import { NotificationInstance } from 'antd/lib/notification/interface';

import IBaseComponentProps from '@components/base-component-props';

import { exception } from '@extensions/notification';

import { userLoaded } from '@store/actions';
import { useAppDispatch } from '@store/hooks';

import { getEnumList } from '@extensions/utils';

import { ITransactionParams } from '@entities/transaction-params';
import { ICounterparty } from '@core/entities/counterparty';
import { ICounterpartyFilter } from '@core/entities/counterparty-filter';
import { IUser } from '@entities/user';
import { ICompany } from '@entities/company';

import { OperationType, enumLabel as enumOperationLabel } from '@enums/operation-type';
import { Currency, enumLabel as currencyLabel } from '@enums/currency';
import { IEnumItem } from '@enums/enum-item';
import { UserBalanceType } from '@enums/user-balance-type';

dayjs.extend(utc);

interface IDepositin extends IBaseComponentProps {
    userId: string;
    userLogin: string;
    userBalanceType?: UserBalanceType;
    onSave: () => void;
    onCancel: () => void;
    api: NotificationInstance;
}

const DepositingModal = (props: IDepositin) => {
    const { api, userLogin, userId, userBalanceType, onSave, onCancel, serverFetch } = props;

    const { TextArea } = Input;

    const [form] = Form.useForm();
    const operationType = Form.useWatch<OperationType>('operationType', form);

    const d = useAppDispatch();

    const [operationTypes] = useState<Array<IEnumItem>>(getEnumList(OperationType, enumOperationLabel));
    const [currentOperation, setCurrentOperation] = useState<OperationType>();

    const [currencies] = useState<Array<IEnumItem>>(getEnumList(Currency, currencyLabel));
    const [currentCurrency, setCurrentCurrency] = useState<Currency>();

    const [counterparties, setCounterparties] = useState<Array<ICounterparty>>([]);
    const [companies, setCompanies] = useState<Array<ICompany>>([]);
    const [processing, setProcessing] = useState<boolean>(false);

    const [user, setUser] = useState<IUser>();

    useEffect(() => {
        const currency = currentOperation == OperationType.Claim ? Currency.Usd : Currency.Rub;
        form.setFieldsValue({
            userId: userId,
            currency: currency,
        });

        setCurrentCurrency(currency);

        const filter: ICounterpartyFilter = {
            userId: userId,
        };

        let cleanup = false;

        const fetchData = async () => {
            const promises = [
                await serverFetch(`users/${userId}`, { method: 'GET' })
                    .then((data: any) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения пользователя', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch('counterparties', { method: 'GET', queryParams: filter })
                    .then((data: any) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения контрагентов', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch('companies', { method: 'GET', queryParams: filter })
                    .then((data: any) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения компаний', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                setUser(result[0][0]);
                setCounterparties(result[0][1]);
                setCompanies(result[0][2]);
            });
        };

        fetchData();

        return () => {
            cleanup = true;
        };
    }, []);

    useEffect(() => {
        if (user && user.userSettings) {
            if (operationType == OperationType.NonCash) {
                form.setFieldValue('commission', user.userSettings.nonCashTransferCommission);
            } else if (operationType == OperationType.Cash) {
                form.setFieldValue('commission', user.userSettings.cashTransferCommission);
            } else {
                form.setFieldValue('commission', undefined);
            }
        }
    }, [user, operationType]);

    const onDepositin = (entity: ITransactionParams) => {
        if (!entity) return;

        entity.userBalanceType = userBalanceType;

        setProcessing(true);

        const path: string = currentOperation == OperationType.Claim ? `transactions/claim` : `transactions/depositing`;
        serverFetch(path, { method: 'POST', bodyData: entity })
            .then(() => {
                onSave();
            })
            .catch((ex: any) => {
                setProcessing(false);
                exception(api, 'Ошибка внесения денежных средств', ex, () => d(userLoaded(undefined)));
            });
    };

    return (
        <Modal
            destroyOnHidden={true}
            width={650}
            title={`Внести средства на счет "${userLogin}"`}
            open={true}
            okText='ОК'
            cancelButtonProps={{ disabled: processing }}
            okButtonProps={{ loading: processing, disabled: processing }}
            onOk={() => {
                form.submit();
            }}
            onCancel={() => onCancel()}
        >
            <Form
                colon={false}
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 18 }}
                style={{ marginTop: 20 }}
                form={form}
                onFinish={onDepositin}
            >
                <Form.Item hidden name='userId'>
                    <Input />
                </Form.Item>
                <Form.Item hidden name='currency'>
                    <Input />
                </Form.Item>
                <Form.Item name='operationType' label='Платеж' rules={[{ required: true, message: 'Укажите тип платежа' }]}>
                    <Select
                        allowClear
                        onChange={(value: OperationType) => {
                            if (value == OperationType.Claim) {
                                setCurrentCurrency(Currency.Usd);
                                form.setFieldValue('currency', Currency.Usd);
                            }

                            setCurrentOperation(value);
                        }}
                        options={operationTypes.map((t) => {
                            return { value: t.value, label: t.label };
                        })}
                    />
                </Form.Item>
                {currentOperation == OperationType.NonCash && (
                    <>
                        <Form.Item name='counterpartyId' label='Контрагент' rules={[{ required: true, message: 'Укажите контрагента' }]}>
                            <Select
                                allowClear
                                options={counterparties.map((t) => {
                                    return { value: t.id, label: `${t.inn} (${t.legalName})` };
                                })}
                            />
                        </Form.Item>
                        <Form.Item name='companyId' label='Компания' rules={[{ required: true, message: 'Укажите компанию получателя' }]}>
                            <Select
                                allowClear
                                options={companies.map((c) => {
                                    return { value: c.id, label: c.legalName };
                                })}
                            />
                        </Form.Item>
                    </>
                )}

                <Form.Item label='Сумма' name='preAmount' wrapperCol={{ span: 9 }} rules={[{ required: true, message: 'Укажите сумму' }]}>
                    <Space.Compact>
                        <InputNumber
                            precision={2}
                            decimalSeparator=','
                            min={0}
                            style={{ width: '100%' }}
                            onChange={(value: number | null) => {
                                if (!value) {
                                    form.setFieldValue('amount', value);
                                    return;
                                }

                                const commission = form.getFieldValue('commission');

                                if (commission) {
                                    const result = value - value * commission * 0.01;
                                    form.setFieldValue('amount', result);
                                } else {
                                    form.setFieldValue('amount', value);
                                }
                            }}
                        />
                        <Select
                            value={currentCurrency}
                            style={{ width: 90 }}
                            disabled={currentOperation == OperationType.Claim}
                            onChange={(value: Currency) => {
                                setCurrentCurrency(value);
                                form.setFieldValue('currency', value);
                            }}
                            options={currencies.map((t) => {
                                return { value: t.value, label: t.label };
                            })}
                        />
                    </Space.Compact>
                </Form.Item>
                {(currentOperation == OperationType.Cash || currentOperation == OperationType.NonCash) && (
                    <Form.Item label='Комиссия'>
                        <Space.Compact>
                            <Form.Item name='commission' noStyle>
                                <InputNumber
                                    decimalSeparator=','
                                    precision={2}
                                    min={0}
                                    style={{ width: '50%' }}
                                    onChange={(value: number | null) => {
                                        const preAmount = form.getFieldValue('preAmount');

                                        if (!preAmount || !value) {
                                            form.setFieldValue('amount', preAmount);
                                        } else {
                                            const result = preAmount - preAmount * value * 0.01;
                                            form.setFieldValue('amount', result);
                                        }
                                    }}
                                />
                            </Form.Item>
                            <Space.Addon>%</Space.Addon>
                        </Space.Compact>
                    </Form.Item>
                )}
                <Form.Item name='amount' label='Итого'>
                    <InputNumber
                        decimalSeparator=','
                        disabled={true}
                        precision={2}
                        min={0}
                        style={{ width: '50%', background: '#f6fefa' }}
                    />
                </Form.Item>
                <Form.Item
                    label='Комментарий'
                    name='comment'
                    rules={[{ required: operationType == OperationType.System, message: 'Укажите комментарий' }]}
                >
                    <TextArea rows={4} />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default DepositingModal;
